import { FC, useMemo, useState } from "react";
import { useAtomValue } from "jotai";
import { useUser } from "reactfire";

import {
  BinStatusEnum,
  ServiceTaskTypeEnum,
  TaskDriverStatusEnum,
} from "@/domain/models/common";
import { useTranslator } from "@/i18n/useTranslator";

import { useUpdateBinMutation } from "../data/mutations/binMutations";
import { useUpdateServiceMutation } from "../data/mutations/serviceMutations";
import { useUpdateTaskMutation } from "../data/mutations/taskMutations";
import { useBinsListQuery } from "../data/queries/binQueries";
import { useServiceQuery } from "../data/queries/serviceQueries";
import { useTaskQuery } from "../data/queries/taskQueries";
import { JobServiceTaskBaseType } from "../domain/task";
import { isDriverReviewingTaskAtom } from "../stores/atoms";
import { sortBinsByIdentifier } from "../utils/bin";
import { generateFirebaseTimestamp } from "../utils/date";
import { DriverScreenWithSelectBox } from "./DriverScreenWithSelectBox";

const DriverSelectBinForService: FC<JobServiceTaskBaseType> = ({
  jobId,
  serviceId,
  taskId,
}) => {
  const { t } = useTranslator();
  const isDriverReviewingTask = useAtomValue(isDriverReviewingTaskAtom);

  const { data: taskData } = useTaskQuery({ jobId, serviceId, taskId });

  const { data: serviceData } = useServiceQuery({ jobId, serviceId });

  const [selectedBin, setSelectedBin] = useState("");

  const { data: userData } = useUser();

  const { data: availableBins } = useBinsListQuery({
    filters: {
      deleted: false,
      status: BinStatusEnum.AVAILABLE,
      size: serviceData?.size,
    },
  });

  const isWaitAndLoadBinTask = taskId === ServiceTaskTypeEnum.WAITANDLOADBIN;

  const binsOptions = useMemo(() => {
    const sortedBins = sortBinsByIdentifier(availableBins ?? [])?.map(bin => ({
      label: bin.identifier,
      value: bin.id,
    }));

    sortedBins?.unshift({
      label: t("select_bin_for_service.placeholder"),
      value: "",
    });

    return sortedBins;
  }, [availableBins, t]);

  const {
    mutate: updateService,
    isPending: isUpdatingService,
    variables,
  } = useUpdateServiceMutation();
  const { mutate: updateTask, isPending: isUpdatingTask } =
    useUpdateTaskMutation();

  const { mutate: updateBin } = useUpdateBinMutation();

  const onNextClick = () => {
    const selectedBinData = availableBins?.find(bin => bin.id === selectedBin);

    if (!selectedBinData || !userData || !serviceData) return;

    const driverFlowStatus = isDriverReviewingTask
      ? TaskDriverStatusEnum.TASK_REVIEW
      : isWaitAndLoadBinTask
        ? TaskDriverStatusEnum.WAIT_BIN_FILLED
        : TaskDriverStatusEnum.TAKING_PHOTO_DROPPED_BIN;

    // If the bin selected is different from the current bin assigned to the service, clean the old bin data
    if (
      serviceData.assignedBin?.id &&
      serviceData.assignedBin.id !== selectedBin
    ) {
      updateBin({
        id: serviceData.assignedBin?.id,
        status: BinStatusEnum.AVAILABLE,
        currentJob: null,
        currentService: null,
        currentData: null,
      });
    }

    updateService({
      jobId,
      serviceId,
      driverFlowStatus,
      assignedBin: {
        assignedAt: generateFirebaseTimestamp(),
        ...selectedBinData,
        status: BinStatusEnum.ASSIGNED,
      },
      assignedBy: {
        email: userData.email ?? "",
        uid: userData.uid,
        name: userData.displayName ?? "",
      },
      lastUpdatedBy: {
        email: userData.email ?? "",
        uid: userData.uid,
        displayName: userData.displayName ?? "",
      },
      stage: 1,
      size: selectedBinData.size,
    });

    // Update the bin with the new data
    updateBin({
      id: selectedBin,
      status: BinStatusEnum.ASSIGNED,
      currentJob: serviceData.jobId,
      currentService: serviceId,
      currentData: {
        address: serviceData.address,
        geopoint: serviceData.geopoint,
        jobId,
        serviceId,
        dueAt: taskData?.dueAt ?? null,
        dropAt: taskData?.dueAt ?? null,
        customer: serviceData.customer,
      },
    });

    updateTask({
      jobId,
      serviceId,
      taskId,
      driverFlowStatus,
    });
  };

  const isLoading = isUpdatingService || isUpdatingTask;

  const isNextButtonLoading =
    isLoading &&
    variables?.driverFlowStatus ===
      TaskDriverStatusEnum.TAKING_PHOTO_DROPPED_BIN;
  const isPreviousButtonLoading =
    isLoading &&
    variables?.driverFlowStatus === TaskDriverStatusEnum.ASKING_CAN_DROP_BIN;

  const goToPreviousStep = () => {
    updateService({
      jobId,
      serviceId,
      driverFlowStatus: TaskDriverStatusEnum.ASKING_CAN_DROP_BIN,
    });

    updateTask({
      jobId,
      serviceId,
      taskId,
      driverFlowStatus: TaskDriverStatusEnum.ASKING_CAN_DROP_BIN,
    });
  };

  return (
    <DriverScreenWithSelectBox
      jobId={jobId}
      serviceId={serviceId}
      taskId={taskId}
      title="select_bin_for_service.title"
      titlei18nOptions={{
        stepNumber: isWaitAndLoadBinTask ? 2 : 3,
      }}
      selectBoxProps={{
        items: binsOptions,
        selectedValue: selectedBin,
        onSelect: setSelectedBin,
      }}
      bottomButtonProps={{
        isNextButtonLoading,
        nextButtonTitle: isDriverReviewingTask ? "review" : "next",
        onNextButtonClick: onNextClick,
        isNextButtonDisabled: !selectedBin,
        previousButtonTitle: "select_bin_for_service.back_button",
        onPreviousButtonClick:
          isDriverReviewingTask || isWaitAndLoadBinTask
            ? undefined
            : () => goToPreviousStep(),
        isPreviousButtonLoading,
      }}
    />
  );
};

export { DriverSelectBinForService };
